import { useRef } from 'react';
import type { IReportInfo } from '@/shared/model';
import { useReportStore } from '@/entities/report';

/**
 * Supplies the AFib events tab with the report identifiers and the shared ECG data map it renders from. Reads reportInfo and ecgDataMap off the report store, holds a ref to the heart-rate chart and exposes an arrow-button handler that drives it.
 *
 * @returns An object carrying profileId, studyId, startReport, stopReport, timezoneOffset, ecgDataMap, the heart-rate chart ref and the arrow-button handler.
 */
const useAfibEvents = () => {
  const { profileId, studyId, startReport, stopReport, timezoneOffset } = useReportStore(
    (s) => s.reportInfo,
  ) as IReportInfo;
  const ecgDataMap = useReportStore((s) => s.ecgDataMap);
  const hrChartRef = useRef<any>(null);

  /**
   * Moves the AFib heart-rate chart to the neighbouring minute. Forwards the direction to the chart's imperative handle, doing nothing until the chart ref is attached.
   *
   * @param isNext - True to advance to the next minute, false to step back.
   * @returns Nothing.
   */
  const handleClickArrowButton = (isNext = false) => {
    if (hrChartRef.current) {
      hrChartRef.current.handleClickArrowButton(isNext);
    }
  };

  return {
    profileId,
    studyId,
    startReport,
    stopReport,
    timezoneOffset,
    ecgDataMap,
    hrChartRef,
    handleClickArrowButton,
  };
};

export default useAfibEvents;
